import { jsx } from 'react/jsx-runtime';
import { FeatureFlaggingProvider as FeatureFlaggingProvider$1 } from '@aviv-trak/feature-flagging';
import { useMemo, createContext, use } from 'react';

const FeatureFlaggingContext = /*#__PURE__*/ createContext(null);
const FeatureFlaggingProvider = ({ children, hasUserConsent = false, strategy })=>{
    /*
   * Calls the real strategy's `useData` exactly once, here. Aviv's own `FeatureFlaggingProvider` (rendered below) must never receive the real
   * `strategy` directly, it would invoke `useData` a second time and, for Statsig, double-initialize the client and double the exposure logging.
   */ const { featureFlags, getExperimentVariant, getFeatureFlag, isInitialized } = strategy.useData(hasUserConsent);
    const contextValue = useMemo(()=>({
            featureFlags,
            getExperimentVariant,
            getFeatureFlag,
            isInitialized
        }), [
        featureFlags,
        getExperimentVariant,
        getFeatureFlag,
        isInitialized
    ]);
    const adaptedStrategy = useMemo(()=>({
            getScript: strategy.getScript,
            useData: ()=>({
                    featureFlags
                })
        }), [
        featureFlags,
        strategy.getScript
    ]);
    return /*#__PURE__*/ jsx(FeatureFlaggingContext, {
        value: contextValue,
        children: /*#__PURE__*/ jsx(FeatureFlaggingProvider$1, {
            hasUserConsent: hasUserConsent,
            strategy: adaptedStrategy,
            children: children
        })
    });
};

/**
 * Variable to detect the test environment.
 * Testing runtimes (Jest and Vitest) automatically set the `NODE_ENV` environment variable to `test`.
 * Thanks to the `DefinePlugin` setup, the bundler minifier automatically strips the conditional code statement if NODE_ENV !== "test".
 * @see {@link https://vitest.dev/guide/migration.html#envs Node environment injection}.
 */ process.env.NODE_ENV === "test";
const LANGUAGE_TAGS_VALUES = [
    "de-DE",
    "de-AT",
    "en-GB"
];
new Set(LANGUAGE_TAGS_VALUES);
new Set(LANGUAGE_TAGS_VALUES.map((tag)=>tag.split("-")[0]));
new Set(LANGUAGE_TAGS_VALUES.map((tag)=>tag.split("-")[1]));
/**
 * Utility type that prevents a string from ending with a period,
 * enforcing that the message factory will always append its own terminating period.
 */ /**
 * Low-level factory that formats a two-line message with a labelled cause and a labelled solution.
 * Prefer the higher-level `createErrorMessage`, `createWarningMessage`, or `createNoteMessage`
 * instead of calling this directly.
 * @param configuration The configuration object.
 * @returns The formatted message string.
 */ const createMessage = ({ cause, solution })=>`${cause.header}: ${cause.message}.\n${solution.header}: ${solution.message}.\n`;
/**
 * A factory to format an error message in a consistent way.
 * @param configuration The configuration object.
 * @returns The formatted error message.
 */ const createErrorMessage = ({ cause, solution })=>createMessage({
        cause: {
            header: "❌ An error occurred",
            message: cause
        },
        solution: {
            header: "✅ To solve the issue",
            message: solution
        }
    });
/**
 * A React hook to enforce the Provider definition before using a context.
 * @param scope Metadata describing the context scope (including package and module name).
 * @param context The React `Context` to check.
 * @returns The context value.
 */ const useSafeContext = (scope, context)=>{
    const value = use(context);
    if (value === null) {
        // Apply the fail-fast principle to prevent any issues in production (e.g. the ones with default values
        // extraneous the current deployment target (such as applying `AVIV_THEME` while the current brand is SeLoger))
        throw new Error(createErrorMessage({
            cause: `The \`${scope.module}\` module is not able to retrieve contextual data`,
            solution: `Verify your Shell integration by checking if the \`${scope.package}\` package is effectively shared.\nIf the issue is only reproducible in your local environment, make sure to simulate the Shell environment with the right component(s)`
        }));
    }
    return value;
};

const useFeatureFlagging = ()=>{
    const { featureFlags, getExperimentVariant, getFeatureFlag, isInitialized } = useSafeContext({
        module: "useFeatureFlagging",
        package: "@iwt-core-web/feature-flagging"
    }, FeatureFlaggingContext);
    return {
        /**
     * Get an experiment value.
     * @param key The experiment key.
     * @param fallbackValue Returned when the experiment cannot be resolved (e.g., pending initialization, no assigned group, or an error).
     */ getExperimentVariant: (key, fallbackValue)=>{
            /*
       * The following condition is necessary to allow legacy interoperability since, on Kameleoon strategy, the getExperimentVariant is not defined.
       * TODO: condition to remove (inline directly the getXXX statement) during Kameleoon decommissioning.
       */ const value = getExperimentVariant ? getExperimentVariant(key) : featureFlags?.[key];
            return value ?? fallbackValue;
        },
        /**
     * Get a feature flag value.
     * @param key The feature flag/gate key.
     * @param fallbackValue Returned when the flag cannot be resolved (e.g., pending initialization, no assigned group, or an error).
     */ getFeatureFlag: (key, fallbackValue)=>{
            /*
       * The following condition is necessary to allow legacy interoperability since, on Kameleoon strategy, the getExperimentVariant is not defined.
       * TODO: condition to remove (inline directly the getXXX statement) during Kameleoon decommissioning.
       */ const value = getFeatureFlag ? getFeatureFlag(key) : Boolean(featureFlags?.[key]);
            return value ?? fallbackValue;
        },
        /**
     * Get available feature flags.
     * @deprecated Use `getFeatureFlag`/`getExperimentVariant` instead, only populated for the Kameleoon strategy and always `undefined` for Statsig strategy.
     */ getFeatureFlags: ()=>featureFlags,
        /**
     * @deprecated Use `getFeatureFlag` instead.
     */ getVariation: (key)=>featureFlags?.[key],
        /**
     * Get the initialization status.
     * @returns `true` if the client is initialized, `false` otherwise.
     */ isInitialized: isInitialized ?? Boolean(featureFlags),
        /**
     * @deprecated Use `isInitialized` instead.
     */ isLoaded: isInitialized ?? Boolean(featureFlags)
    };
};

export { FeatureFlaggingProvider, useFeatureFlagging };
